import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../../mocks/blog';
import LanguageSelector, { LangCode, getTranslation } from '../../components/feature/LanguageSelector';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState<LangCode>('es');

  const t = (key: string) => getTranslation(lang, key);

  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{t('blogpost_not_found')}</h1>
          <Link to="/blog" className="text-red-600 hover:text-red-700">
            {t('blogpost_back_blog')}
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  if (relatedPosts.length < 3) {
    const additionalPosts = blogPosts
      .filter(p => p.id !== post.id && !relatedPosts.includes(p))
      .slice(0, 3 - relatedPosts.length);
    relatedPosts.push(...additionalPosts);
  }

  const pageUrl = encodeURIComponent(window.location.href);
  const pageTitle = encodeURIComponent(post.title);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${pageTitle}%20${pageUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${pageTitle}&url=${pageUrl}`,
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatContent = (content: string) => {
    const lines = content.split('\n').filter(line => line.trim());
    return lines.map((line, index) => {
      if (line.startsWith('•')) {
        return (
          <li key={index} className="ml-6 mb-2">
            {line.substring(1).trim()}
          </li>
        );
      }
      if (line.length < 100 && !line.includes('.') && index > 0 && index < lines.length - 1) {
        return (
          <h3 key={index} className="text-xl font-bold mt-8 mb-4 text-gray-900">
            {line}
          </h3>
        );
      }
      return (
        <p key={index} className="mb-4 leading-relaxed">
          {line}
        </p>
      );
    });
  };

  const ShareBar = ({ label }: { label: string }) => (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide mr-1">{label}</span>
      <a
        href={shareLinks.whatsapp}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
      >
        <i className="ri-whatsapp-line text-base"></i>
        WhatsApp
      </a>
      <a
        href={shareLinks.facebook}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="flex items-center gap-2 bg-[#1877F2] text-white px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
      >
        <i className="ri-facebook-fill text-base"></i>
        Facebook
      </a>
      <a
        href={shareLinks.twitter}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
      >
        <i className="ri-twitter-x-line text-base"></i>
        X / Twitter
      </a>
      <button
        onClick={handleCopy}
        className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className={copied ? 'ri-check-line text-green-600' : 'ri-links-line text-base'}></i>
        {copied ? t('blogpost_copied') : t('blogpost_copy')}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                <i className="ri-home-heart-fill text-white text-2xl"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">Red Lions Realty</span>
            </Link>

            <div className="flex items-center gap-6">
              <Link to="/" className="text-gray-700 hover:text-red-600 transition-colors font-medium hidden sm:block">
                {t('blogpost_nav_home')}
              </Link>
              <Link to="/blog" className="text-red-600 font-medium hidden sm:block">
                {t('blogpost_nav_blog')}
              </Link>
              <LanguageSelector currentLang={lang} onChangeLang={setLang} scrolled={true} />
              <button
                onClick={() => navigate('/')}
                className="bg-red-600 text-white px-6 py-2.5 rounded-xl hover:bg-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                {t('blogpost_nav_contact')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Image */}
      <div className="relative h-[500px] mt-20">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors mb-8 group cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
          <span className="font-medium">{t('blogpost_back')}</span>
        </button>

        {/* Meta Info */}
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <span className="bg-red-100 text-red-600 px-4 py-1.5 rounded-full text-sm font-medium">
            {post.category}
          </span>
          <span className="text-gray-500 flex items-center gap-2">
            <i className="ri-time-line"></i>
            {post.readTime}
          </span>
          <span className="text-gray-500 flex items-center gap-2">
            <i className="ri-calendar-line"></i>
            {new Date(post.date).toLocaleDateString('es-ES', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Share Bar Top */}
        <div className="mb-10 pb-8 border-b border-gray-100">
          <ShareBar label={t('blogpost_share')} />
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div className="text-gray-700 text-lg">
            {formatContent(post.content)}
          </div>
        </div>

        {/* Share Bar Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <ShareBar label={t('blogpost_share_liked')} />
        </div>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('blogpost_cta_title')}
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            {t('blogpost_cta_sub')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-8 py-3 rounded-xl hover:bg-red-700 transition-colors font-medium whitespace-nowrap inline-flex items-center gap-2 cursor-pointer"
          >
            <span>{t('blogpost_cta_btn')}</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              {t('blogpost_related')}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
                        {relatedPost.category}
                      </span>
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {relatedPost.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-red-600 font-medium group-hover:gap-3 transition-all">
                      <span>{t('blogpost_read_article')}</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gradient-to-br from-red-600 to-orange-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-home-heart-fill text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold">Red Lions Realty</span>
              </div>
              <p className="text-white/80 text-sm">{t('blogpost_footer_tagline')}</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('blogpost_footer_contact')}</h4>
              <div className="space-y-2 text-sm text-white/80">
                <p className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  +34 744 780 446
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  redlionsl.24@gmail.com
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('blogpost_footer_offices')}</h4>
              <div className="space-y-3 text-sm text-white/80">
                <p>Local Hurchillo, Orihuela</p>
                <p>Local Centro Orihuela (frente al Casino)</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('blogpost_footer_follow')}</h4>
              <div className="flex gap-3">
                <a href="#" rel="nofollow" className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/redlionsrealty" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://www.tiktok.com/@red.lions.realty" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors">
                  <i className="ri-tiktok-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center text-sm text-white/60">
            <p>{t('blogpost_footer_rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
